<template>
  <div id="app">
    <!-- screen reader announcer about route change -->
    <div id="announcer" tabindex="0" class="sr-only" />

    <HelperSvgIcons aria-hidden="true" />
    <ElemLoader v-if="loaderOpen" />
    <ElemModal v-if="modalOpen" />
    <ElemModalSmall v-if="modalSmallOpen" />

    <Header />

    <main class="container">
      <div class="row">
        <!-- content -->
        <div class="col-sm-12 col-md-12 col-lg-10 offset-lg-1">
          <router-view :key="router.currentRoute.value.name" />
        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { RouterView } from 'vue-router';

import ApiCalls from '@/classes/ApiCalls';
import Helpers from '@/classes/Helpers';
import TrackClient from '@/classes/TrackClient';
import ElemLoader from '@/components/elements/ElemLoader.vue';
import ElemModal from '@/components/elements/ElemModal.vue';
import ElemModalSmall from '@/components/elements/ElemModalSmall.vue';
import HelperSvgIcons from '@/components/elements/ElemSvgIcons.vue';
import Footer from '@/components/sections/SectionFooter.vue';
import Header from '@/components/sections/SectionHeader.vue';
import router from '@/router';
import global from '@/store/global';
import modal from '@/store/modal';
import modalSmall from '@/store/modalSmall.js';
import { loadLanguage } from '@/translations/translation-setup';

import { app, i18n } from './main.js';

const { t } = useI18n();

// CLASSES
const apiCalls = new ApiCalls();
let trackClient!: TrackClient;

// token refresh interval
let tokenRefresh!: any;

// params
const params = Helpers.paramsToObject(false);

// LIFECYCLES
// offline listeners
window.addEventListener('offline', () => {
  setPageOnlineStatus(false);
});
window.addEventListener('online', () => {
  setPageOnlineStatus(true);
});

// store the page status
const pageOnline = window.navigator.onLine;

nextTick(() => {
  setPageOnlineStatus(pageOnline);
});

// set app language
const { lang } = params as any;
loadLanguage(lang);

onBeforeUnmount(() => {
  // stop client tracking and refreshing token
  if (trackClient) {
    trackClient.stopTracking();
    clearInterval(tokenRefresh);
  }

  window.removeEventListener('offline', () => {
    setPageOnlineStatus(false);
  });
  window.removeEventListener('online', () => {
    setPageOnlineStatus(true);
  });
});

// COMPUTED
const modalOpen = computed(() => {
  return modal.modalOpened;
});

const modalSmallOpen = computed(() => {
  return modalSmall.modalOpened;
});

const loaderOpen = computed(() => {
  return global.loaderOpened;
});

// WATCHERS
// set locale on localization plugin
watch(
  () => global.language,
  () => {
    // check if the locale is available
    const localeAvailable = i18n.global.availableLocales.includes(
      global.language,
    );

    // if locale isnt available, use fallback locale
    // (on dev is fallback undefined, thats fine)
    if (localeAvailable) {
      i18n.global.locale.value = global.language;
    } else {
      i18n.global.locale.value = i18n.fallbackLocale.toString();
    }
  },
);

watch(
  () => router.currentRoute.value,
  () => {
    setScreenReaderAnnoucement();
    setScreenToTop();
  },
);

// METHODS
const initApp = async (): Promise<void> => {
  // if (import.meta.env.NODE_ENV === 'development') {
  //     // turn on loader
  //     global.loaderOpened = false;
  //     apiCalls.getClientInfo().then((result) => {
  //         if (result.success) {
  //             stateClientInfo = result.resultMessage;
  //         } else {
  //             modal.modalType = 'techError';
  //             modal.modalOpened = true;
  //             global.loaderOpened = false;
  //         }
  //     }).catch((error) => {
  //         modal.modalType = 'techError';
  //         modal.modalOpened = true;
  //         global.loaderOpened = false;
  //     });
  //
  //     return;
  // }

  /**
   * http://slsp.localhost:1234/sporopay-kluc#access_token=3S3FUYFBLZCEJSIJMNADGM3W44AQNUQR&state=||16b20e0d-5725-4253-8db8-f853454ee000&lang=sk
   * http://slsp.localhost:1234?state=||16b20e0d-5725-4253-8db8-f853454ee000
   */

  // get auth token
  let authToken = (params as any).access_token;
  const authCode = (params as any).code;

  if (
    sessionStorage.getItem('id_token') &&
    sessionStorage.getItem('expires_in')
  ) {
    if (new Date() > new Date(sessionStorage.getItem('expires_in')!)) {
      sessionStorage.removeItem('id_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('expires_in');
    } else {
      global.authType = 'OIDC';
      global.idToken = sessionStorage.getItem('id_token')!;
      global.refreshToken = sessionStorage.getItem('refresh_token')!;
      authToken = sessionStorage.getItem('access_token');

      global.refreshTokenIntervalId = setInterval(() => {
        if (new Date() > new Date(sessionStorage.getItem('expires_in')!)) {
          apiCalls.refreshOIDCtoken();
        }
      }, 1000);
    }
  }

  if (authCode) {
    // new auth approach - OIDC (implemented to support redirect from George Business)
    // handle getting auth token from code
    global.authType = 'OIDC';
    global.idToken = sessionStorage.getItem('id_token')!;

    const data = await apiCalls.getOIDCtoken(authCode);

    authToken = data.resultMessage.access_token;
    sessionStorage.setItem('access_token', data.resultMessage.access_token);

    global.refreshToken = data.resultMessage.refresh_token;
    sessionStorage.setItem('refresh_token', data.resultMessage.refresh_token);
  }

  // if we have authtoken, do the stuff related to auth client
  if (authToken) {
    // store auth token
    global.authToken = authToken;

    // handle CSRF
    let csrfState = Helpers.parseState((params as any).state)[1] || '_';
    let csrfCookie =
      app.$cookies.get('_csrf') || app.$cookies.get('gt_csrf') || '~';

    if (import.meta.env.NODE_ENV === 'development') {
      csrfState = 'true';
      csrfCookie = 'true';
    }

    if (csrfState !== csrfCookie && global.authType !== 'OIDC') {
      modal.modalType = 'techError';
      modal.modalOpened = true;
      apiCalls.invalidateToken();
    } else {
      // remove csrf cookie
      app.$cookies.remove('_csrf');
      app.$cookies.remove('gt_csrf');

      // set referer
      // eslint-disable-next-line
      global.referer = Helpers.parseState((params as any).state)[0];

      // VII
      apiCalls
        .getClientInfo()
        .then((result) => {
          if (result.success) {
            global.clientInfo = result.resultMessage;
          } else {
            modal.modalType = 'techError';
            modal.modalOpened = true;
            global.loaderOpened = false;
          }
        })
        .catch(() => {
          modal.modalType = 'techError';
          modal.modalOpened = true;
          global.loaderOpened = false;
        });

      // start user tracking
      if (import.meta.env.NODE_ENV !== 'development') {
        trackClient = new TrackClient(300, 60, () => {
          apiCalls.invalidateToken();
        });
        trackClient.startTracking();
      }

      const intervalInMinutes = 1;
      // refresh token to keep user active while using page, but not making backend calls.
      tokenRefresh = setInterval(
        () => {
          apiCalls.refreshToken();
        },
        1000 * 60 * intervalInMinutes,
      ); // 4 min

      // assign custom events
      // @ts-ignore
      window.eFormReset = new CustomEvent('eFormReset'); // form reset event
    }
  } else {
    const sourceCookie = app.$cookies.get('_source') || '';
    global.referer = sourceCookie;
  }

  // remove params from url
  setTimeout(
    () =>
      window.history.replaceState('', document.title, window.location.pathname),
    2000,
  );

  // set flag about app start
  global.appStarted = true;
};

// stores the status on online/offline
const setPageOnlineStatus = (status: boolean): void => {
  global.isPageOnline = status;
  nextTick(() => {
    handlePageOnlineStatus();
  });
};

// handles what should happens when page goes offline/online
const handlePageOnlineStatus = () => {
  modal.modalOpened = false;

  // if we are online, start the app, else stop tracking and show modal
  if (global.isPageOnline) {
    if (!global.appStarted) {
      initApp();
    }
  } else {
    nextTick(() => {
      // show modal
      if (trackClient) {
        trackClient.stopTracking();
      }
      global.loaderOpened = false;
      modal.modalType = 'offline';
      modal.modalOpened = true;
    });
  }
};

// set screen reader announcer of page change
const setScreenReaderAnnoucement = () => {
  // set screen reader announcer of page change
  const currentLanguage = i18n.global.locale.value;
  const currentPage =
    router.currentRoute.value.meta?.accessibility.currentPage[
      currentLanguage
    ] || '';
  const accessibilityNavigation = t('accessibility.currentPage').toString();
  const announcer = document.getElementById('announcer');

  if (announcer) {
    announcer.innerHTML = `${accessibilityNavigation}${currentPage}`;
    announcer.focus();
  }
};

// shift screen to top of screen
// eslint-disable-next-line
    const setScreenToTop = () => {
  window.scrollTo(0, 0);
};
</script>

<style lang="scss">
@use '@/assets/scss/_reset.scss';
@use '@/assets/scss/_keyframes.scss';
@use '@/assets/scss/global.scss';
@use '@/assets/scss/_typography.scss';
@use '@/assets/scss/_buttons.scss';
@use '@/assets/scss/_pageElements.scss';
@use '@/assets/scss/_forms.scss';
@use '@/assets/scss/_dashboard.scss';
@use '@/assets/scss/_signing.scss';
@use '@/assets/scss/_processCard.scss';
@use '@/assets/scss/_infoTable.scss';
@use '@/assets/scss/_infoBlock.scss';
@use '@/assets/scss/_showHide.scss';
@use '@/assets/scss/_paymentHistory.scss';
@use '@/assets/scss/variables.scss';
@use '@/assets/scss/mixins.scss';

#app {
  position: relative;

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }

  main.container {
    display: block;

    // since header and footer are fixed, move it from its height
    padding-top: mixins.calculateRem(variables.$headerHeight);
    padding-bottom: mixins.calculateRem(variables.$headerHeight - 20);
  }
}
</style>
